import { Box, Button, CircularProgress, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { ValidationMessage } from 'tools/constants/payment';
import { ThemeMode } from 'tools/constants/profile';

import { getErrorMsgFromAPIResponse } from 'helpers/errors';

import IxoPayCardForm from 'components/payment/IxoPayCardForm';
import type { IxoPayCardFormRef } from 'components/payment/IxoPayCardForm';
import Spinner from 'components/spinners/Spinner';

import { useThemeMode } from 'hooks';

const SET_IXOPAY_USER_DATA = 'SET_IXOPAY_USER_DATA';

interface MobileData {
  publicIntegrationKey?: string;
}

declare global {
  interface Window {
    /** This interface can be used to let mobile know the app is ready */
    FormReady?: {
      postMessage: (data: string) => void;
    };
    OnCancel?: {
      postMessage: (data: string) => void;
    };
    OnError?: {
      postMessage: (data: string) => void;
    };
    OnToken?: {
      postMessage: (data: string) => void;
    };
    /** This function is used to enable the mobile app to set the form default data */
    setUserData: (
      /** This parameter is a json of the mobile data, represented on the `MobileData` interface */
      data: string,
    ) => void;
  }
}

// Assigned to window on mount, never at module scope: every webview module
// evaluates at app boot, so a module-scope assignment is clobbered by
// whichever module evaluates last.
const handleSetUserData = (data: string) => {
  let parsed: MobileData = {};
  try {
    parsed = JSON.parse(data) as MobileData;
  } catch {
    // Malformed payload — keep defaults (no key).
  }
  window.dispatchEvent(
    new CustomEvent(SET_IXOPAY_USER_DATA, { detail: parsed }),
  );
};

/** IxoPay card-form page embedded by the mobile app — tokenizes the card and posts it back over `OnToken`; the host saves it. */
const IxoPayAddPaymentMethod = () => {
  const { enqueueSnackbar } = useSnackbar();
  const cardFormRef = useRef<IxoPayCardFormRef>(null);

  const [publicIntegrationKey, setPublicIntegrationKey] = useState<
    string | null
  >(null);
  const [isCardFormReady, setIsCardFormReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { 1: setThemeMode } = useThemeMode();

  useEffectOnce(() => {
    const onUserData = (event: Event) => {
      const detail = (event as CustomEvent).detail as MobileData;
      setPublicIntegrationKey(detail.publicIntegrationKey ?? null);
    };
    setThemeMode(ThemeMode.Light);
    window.setUserData = handleSetUserData;
    window.addEventListener(SET_IXOPAY_USER_DATA, onUserData);
    // Only invite the app to inject once this page owns the contract.
    window.FormReady?.postMessage?.('');
    return () => {
      // cleaning up custom events listener
      window.removeEventListener(SET_IXOPAY_USER_DATA, onUserData);
    };
  });

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { cardHolder, expiry, token } = await cardFormRef.current!.submit();
      window.OnToken?.postMessage?.(
        JSON.stringify({ expiry, name: cardHolder, token }),
      );
    } catch (error) {
      const message =
        getErrorMsgFromAPIResponse(error) ||
        ValidationMessage.SOMETHING_WENT_WRONG;
      window.OnError?.postMessage?.(message);
      enqueueSnackbar(message);
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    window.OnCancel?.postMessage?.('Cancel');
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 3 }}>
        {!isCardFormReady && <Spinner />}

        <IxoPayCardForm
          onReady={setIsCardFormReady}
          publicIntegrationKey={publicIntegrationKey}
          ref={cardFormRef}
          showSetAsDefault={false}
          stacked
        />

        <Box display="flex" flexDirection="column" gap={2} mt={4}>
          <Button
            color="primary"
            disabled={isSubmitting || !isCardFormReady}
            fullWidth
            onClick={handleConfirm}
            sx={{ borderRadius: '8px' }}
            variant="contained"
          >
            {isSubmitting ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              'Add Payment Method'
            )}
          </Button>
          <Button
            disabled={isSubmitting}
            fullWidth
            onClick={onCancel}
            sx={{ borderRadius: '8px' }}
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default IxoPayAddPaymentMethod;
